import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminCreateUser, adminListUsers, adminResetPassword, adminUpdateUser } from '../api';
import { useAuth } from '../auth';
import { useT } from '../i18n';
import PageHeader from '../components/PageHeader';
import Notice from '../components/Notice';
import type { Role, User } from '../types';

export default function Admin() {
  const qc = useQueryClient();
  const { user: self } = useAuth();
  const { t } = useT();
  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: adminListUsers });
  const [reveal, setReveal] = useState<{ username: string; password: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['admin', 'users'] }); qc.invalidateQueries({ queryKey: ['players'] }); qc.invalidateQueries({ queryKey: ['rankings'] }); };
  const create = useMutation({ mutationFn: adminCreateUser, onSuccess: (r) => { setReveal({ username: r.user.username, password: r.tempPassword }); setError(null); invalidate(); }, onError: (e: Error) => setError(e.message) });
  const update = useMutation({ mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof adminUpdateUser>[1] }) => adminUpdateUser(id, patch), onSuccess: () => { setError(null); invalidate(); }, onError: (e: Error) => setError(e.message) });
  const reset = useMutation({ mutationFn: (u: User) => adminResetPassword(u.id).then((r) => ({ username: u.username, password: r.tempPassword })), onSuccess: (r) => { setReveal(r); setError(null); }, onError: (e: Error) => setError(e.message) });

  return (
    <>
      <PageHeader eyebrow={t('nav.admin')} title={t('a.title')} subtitle={t('a.sub')} />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,22rem)_1fr]">
        <CreateUserForm busy={create.isPending} onSubmit={(input) => create.mutate(input)} />
        <div className="space-y-3">
          {error && <Notice kind="error">{error}</Notice>}
          {reveal && (
            <Notice kind="success">
              <div className="font-medium">{t('a.temp.title', { u: reveal.username })}</div>
              <div className="font-mono text-lg mt-1 select-all" data-allow-select>{reveal.password}</div>
              <div className="text-xs opacity-80 mt-1">{t('a.temp.hint')}</div>
            </Notice>
          )}
          <div className="text-xs text-dim">{t('a.hc.hint')}</div>
          <ul className="card divide-y divide-ink-700">
            {(users.data?.users ?? []).map((u) => (
              <li key={u.id} className={`px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 ${u.active ? '' : 'opacity-50'}`}>
                <div className="min-w-0 basis-full sm:basis-auto sm:flex-1">
                  <div className="font-medium truncate">
                    {u.displayName} <span className="text-dim font-normal">@{u.username}</span>
                    {u.mustChangePassword && <span className="pill bg-ink-800 text-dim ml-2">{t('a.temppw')}</span>}
                  </div>
                  <div className="text-xs text-dim truncate">{u.email ?? t('a.noemail')} · {u.lastLoginAt ? t('a.lastseen', { d: new Date(u.lastLoginAt).toLocaleDateString() }) : t('a.never')}</div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1 text-xs text-dim">{t('a.hc')}
                    <input type="number" min={-6} max={6} defaultValue={u.handicap} className="w-14 py-1.5 px-2 text-center stat"
                      onBlur={(e) => { const h = Number(e.target.value); if (Number.isInteger(h) && h !== u.handicap) update.mutate({ id: u.id, patch: { handicap: h } }); }} />
                  </label>
                  <select className="w-auto text-sm py-1.5" value={u.role} disabled={u.id === self?.id} onChange={(e) => update.mutate({ id: u.id, patch: { role: e.target.value as Role } })}>
                    <option value="player">{t('a.role.player')}</option><option value="admin">{t('a.role.admin')}</option>
                  </select>
                  <button className="btn-secondary text-sm py-1.5" onClick={() => reset.mutate(u)}>{t('a.reset')}</button>
                  <button className={`${u.active ? 'btn-danger' : 'btn-secondary'} text-sm py-1.5`} disabled={u.id === self?.id} onClick={() => update.mutate({ id: u.id, patch: { active: !u.active } })}>
                    {u.active ? t('a.disable') : t('a.enable')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}

function CreateUserForm({ busy, onSubmit }: { busy: boolean; onSubmit: (input: { username: string; email: string; displayName?: string; role: Role }) => void }) {
  const { t } = useT();
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('player');
  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({ username, displayName: displayName || undefined, email, role });
    setUsername(''); setDisplayName(''); setEmail('');
  }
  return (
    <form onSubmit={submit} className="card p-5 space-y-4 self-start">
      <div className="font-medium">{t('a.new')}</div>
      <div><label className="label">{t('a.username')}</label><input value={username} onChange={(e) => setUsername(e.target.value)} autoCapitalize="none" required /></div>
      <div><label className="label">{t('a.display')}</label><input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder={t('a.display.ph')} /></div>
      <div><label className="label">{t('a.email')}</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
      <div><label className="label">{t('a.role')}</label>
        <select value={role} onChange={(e) => setRole(e.target.value as Role)}><option value="player">{t('a.role.player')}</option><option value="admin">{t('a.role.admin')}</option></select></div>
      <button className="btn-primary w-full" disabled={busy}>{busy ? t('a.creating') : t('a.create')}</button>
    </form>
  );
}
